import { createPortal } from 'react-dom';

/**
 * Google Calendar "Sync with Google" explanation modal (#3277). Presentational.
 * Backdrop click → onCancel; the card stops propagation so inner clicks don't
 * close it. Connect calls onCancel() then onConnect() in that order.
 */
export interface SyncExplanationModalProps {
  open: boolean;
  onCancel: () => void;
  onConnect: () => void;
}

export function SyncExplanationModal({ open, onCancel, onConnect }: SyncExplanationModalProps) {
  if (!open) return null;
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={() => onCancel()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          width: '435px',
          maxWidth: '90vw',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text)' }}>
              Sync with Google
            </span>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <ul style={{
            margin: '0 0 16px 0',
            paddingLeft: '20px',
            fontSize: '16px',
            color: 'var(--color-text-subtle)',
            lineHeight: 1.8,
            listStyleType: 'disc',
            listStylePosition: 'outside',
          }}>
            <li>Create, edit, and delete events with two way sync</li>
            <li>Selected calendars appear in Xavior</li>
            <li>Read, compose, and reply to emails with Xavior</li>
            <li>Syncs automatically every few minutes or manually anytime</li>
            <li style={{ color: 'var(--color-text-faint)', fontSize: '14px' }}>Your data is stored in an isolated, encrypted database — only you and your AI can access it</li>
          </ul>
        </div>

        <div style={{
          padding: '0 24px 16px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
        }}>
          <button
            onClick={() => onCancel()}
            style={{
              padding: '10px 20px',
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--color-text-subtle)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onCancel();
              onConnect();
            }}
            style={{
              padding: '10px 20px',
              background: 'var(--color-primary)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
            }}
          >
            Connect
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('modal-portal-root') || document.body
  );
}
